import { t, Trans } from '@lingui/macro';
import { X, FileText, ExternalLink, List, EyeOff, Eye, Send, File as FileIcon } from "lucide-react";
import { useReducer, useRef, useState } from "react";
import { DocuBot, DocuBotFile } from "../../../models/DocuBot";
import { DocSummary } from "../../../models/DTO";
import { Room } from "../../../models/Room";
import { UUID } from "../../../models/local/Uid";
import { Fire } from "../../../services/FireClient";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { getMessageText } from "../../components/MessageItem";
import { BotAskModalPopover } from "./BotAskModalPopover";
import BotAskSummaryPart from "./BotAskSummaryPart";
import BotSourceTextModal from "./BotSourceTextModal";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";

export const BotDocuPrivateModal: React.FC<{ bot: DocuBot, room: Room, onClose: () => void }> = ({ bot, room, onClose }) => {
  const [show, setShow] = useState(true);
  const [showSummaries, setShowSummaries] = useState(false);
  const [summaries, setSummaries] = useState<DocSummary[] | null>(null);
  const [fileOpened, setFileOpened] = useState<DocuBotFile | null>();
  const [fileFilter, setFileFilter] = useState<DocuBotFile | null>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState("");
  const lastItem = useRef<HTMLDivElement>(null);
  const fileNames = bot.files.map(f => f.filename);
  const { toast } = useToast();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  async function onPresent() {
    setSummaries(await Fire.getBotSummaries(bot));
  }

  function handleClose() {
    setShow(false);
    setTimeout(onClose, 300);
  }

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0" onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between space-y-0 shrink-0">
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-5 w-5" />
          </Button>
          <DialogTitle className="flex flex-col items-center">
            <span className="text-sm font-semibold">{bot.name}</span>
            <span className="text-xs font-normal text-muted-foreground">#{room.name}</span>
          </DialogTitle>
          <div className="w-5"></div>
        </DialogHeader>

        {fileOpened && (
          <div className="h-[40%] bg-muted border-b shrink-0 relative">
            <iframe src={fileOpened.url} className="w-full h-full" title="PDF Viewer" />
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-2 right-2 opacity-80 hover:opacity-100"
              onClick={() => setFileOpened(null)}
            >
              <Trans>Close Preview</Trans>
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-muted/50 p-4 space-y-4" >
          {/* Welcome Info */}
          <div className="text-center text-sm text-muted-foreground py-4 border-b border-border mb-4 mx-auto max-w-lg">
            {bot.hideFiles ? (
              <p><strong><Trans>Ask questions to {bot.name}</Trans></strong></p>
            ) : (
              <div className="space-y-1">
                <p><strong><Trans>Ask questions that are related to:</Trans></strong></p>
                {fileNames.map(f => <p key={f} className="text-xs">{f}</p>)}
              </div>
            )}
            <p className="mt-2 text-xs opacity-70"><Trans>This conversation will not be saved.</Trans></p>
          </div>

          {messages.map((m) => getMessageItem(m))}
          <div ref={lastItem}></div>
        </div>

        <div className="border-t bg-background p-2 shrink-0 space-y-2">

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-2 overflow-x-auto pb-1">
            <div className="flex items-center gap-2">
              {bot.hideFiles ? (
                <span className="text-sm text-muted-foreground px-2"><Trans>Ask {bot.name}</Trans></span>
              ) : (
                bot.files.length > 0 && (
                  <>
                    {bot.files.length > 1 ? (
                      <Select
                        value={fileFilter?.filename || "all"}
                        onValueChange={(val) => changeFileFilter(val === "all" ? null : bot.files.find(f => f.filename === val) || null)}
                      >
                        <SelectTrigger className="h-8 w-[200px] text-xs">
                          <SelectValue placeholder={t`Ask all ${bot.files.length} documents`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all"><Trans>Ask all {bot.files.length} documents</Trans></SelectItem>
                          {bot.files.map(f => <SelectItem key={f.filename} value={f.filename}>{f.filename}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div
                        className="flex items-center gap-1 cursor-pointer hover:bg-muted p-1 rounded text-sm group"
                        onClick={() => setFileOpened(fileFilter ?? bot.files[0])}
                      >
                        <FileIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate max-w-[150px]">{bot.files[0].filename}</span>
                      </div>
                    )}

                    {fileFilter && !fileOpened && (
                      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setFileOpened(fileFilter ?? bot.files[0])}>
                        <Eye className="h-3 w-3 mr-1" /><Trans>Open</Trans>
                      </Button>
                    )}
                    {fileOpened && (
                      <>
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setFileOpened(null)}>
                          <EyeOff className="h-3 w-3 mr-1" /><Trans>Close</Trans>
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => window.open(fileOpened.url, '_blank')}>
                          <ExternalLink className="h-3 w-3 mr-1" /><Trans>URL</Trans>
                        </Button>
                      </>
                    )}
                  </>
                )
              )}
            </div>

            <div>
              {summaries === null && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              {summaries?.length === 0 && <span className="text-xs text-muted-foreground"><Trans>No outline</Trans></span>}
              {summaries && summaries.length > 0 && (
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setShowSummaries(!showSummaries)}>
                  <List className="h-3 w-3 mr-1" />
                  {showSummaries ? <Trans>Hide</Trans> : <Trans>Outline</Trans>}
                </Button>
              )}
            </div>
          </div>

          {showSummaries && (
            <div className="bg-muted/50 border rounded-md p-2 max-h-40 overflow-y-auto inset-x-0">
              <BotAskSummaryPart summaries={getSummaries()} onClick={(sum) => summarySelected(sum)} />
            </div>
          )}

          <div className="flex gap-2">
            <Input
              id="prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(prompt);
                }
              }}
              placeholder={t`Send a message`}
              className="flex-1"
              autoComplete="off"
            />
            <Button onClick={() => sendMessage(prompt)} size="icon" disabled={!prompt.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  function getMessageItem(m: ChatMessage) {
    return (
      <div key={m.id} className="space-y-2">
        <div className="flex items-start gap-2 justify-end">
          <div className="bg-blue-100 text-blue-900 rounded-lg p-3 max-w-[80%] text-sm whitespace-pre-wrap">
            {m.prompt}
          </div>
          {/* We can use <BotAskModalPopover> here if needed for user message actions too */}
          <BotAskModalPopover bot={bot} msg={m} room={room} />
        </div>

        {m.inProgress && (
          <div className="flex justify-start pl-2">
            <div className="bg-background p-2 rounded-lg shadow-sm border">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}

        {m.answer && (
          <div className="flex justify-start">
            <div className="bg-background rounded-lg p-3 shadow-sm border max-w-[90%] space-y-2">
              <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: getMessageText(m.answer) }}></div>
              <div className="flex justify-end">
                <BotSourceTextModal text={m.answer} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  function getSummaries() {
    if (summaries && fileFilter) {
      return summaries.filter(s => s.filename === fileFilter.filename);
    } else {
      return summaries ?? [];
    }
  }

  async function sendMessage(prompt: string) {
    if (!prompt) {
      alert("Please enter a question");
      return;
    }
    prompt = prompt.trim();
    // setInProgress(true);
    try {
      const finalPrompt = fileFilter ? `/${fileFilter.filename}: ${prompt}` : prompt;
      let m = new ChatMessage(prompt, null)
      setMessages(p => [...p, m])
      setPrompt("");
      scrollViewToBottom();
      let answer: string = await Fire.askDocuBot(bot, finalPrompt);
      m.inProgress = false;
      m.answer = answer;
      forceUpdate();
      scrollViewToBottom();
    } catch (error) {
      const e = error as Error;
      toast({ title: t`Error`, description: e.message });
    }
  }

  function scrollViewToBottom() {
    if (lastItem.current) {
      setTimeout(() => {
        lastItem.current?.scrollIntoView({ behavior: "smooth" });
        //Auto scrolling works bad on iOS. It stops in the middle, and requires multiple attempts to get to the bottom. Yes, this is a hack.
        nrOfScrolls++;
        if (nrOfScrolls <= 3) {
          scrollViewToBottom();
        } else {
          nrOfScrolls = 0;
        }
      }, nrOfScrolls * 100);
      focusInput();
    }
  }

  function changeFileFilter(file: DocuBotFile | null) {
    setFileFilter(file);
  }

  function summarySelected(question: string) {
    setPrompt(question);
    sendMessage(question);
  }

  function focusInput() {
    setTimeout(() => {
      const inputEl = document.getElementById('prompt-input');
      inputEl?.focus();
    }, 200);
  }
}
var nrOfScrolls = 1;

export default BotDocuPrivateModal;

export class ChatMessage {
  id: string = UUID.get();
  created = new Date();
  inProgress = true;
  constructor(public prompt: string, public answer: string | null) {
  }
}
